import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { Drawer, DrawerModule } from 'primeng/drawer';

import { CategoryTemplateCreateCommonFormComponent } from '@offconon/shared/ui/category-template-create-common-form';
import { SharedUiVirtualscrollDropdownComponent } from '@offconon/shared/ui/shared-ui-virtualscroll-dropdown';
import { PaginationAndSearch } from '@offconon/shared/ui/shared-ui-virtualscroll-multiselect';

import { JoinCategoryPathPipe } from '../../pipes/join-category-path.pipe';
import { CategoryPathStoreModule } from '../../store/category-path.module';
import {
  CategoryPathActions,
  CategorySelectOption,
  selectCategoriesCommonSelectOption,
  selectLoading,
  selectSelectedCategoryCommonSelectOption,
} from '../../store/category-path.store';

@Component({
  selector: 'offconon-category-path-dropdown-search-common',
  templateUrl: './category-path-dropdown-search-common.component.html',
  imports: [
    SharedUiVirtualscrollDropdownComponent,
    ButtonModule,
    CategoryTemplateCreateCommonFormComponent,
    JoinCategoryPathPipe,
    TranslateModule,
    DividerModule,
    DrawerModule,
    CategoryPathStoreModule,
  ],
})
export class CategoryPathDropdownSearchCommonComponent implements OnInit, OnChanges {
  @Input() parameters: any;
  @Input() business_id: any;
  @Input() selectFirst = true;
  @Input() description: any;
  @Input() category_type: string | string[];
  @Input() categoryFormControl: FormControl = new FormControl();
  @Input() showClear = false;
  @Input() create_new = true;
  @Output() selectedCategoryEvent = new EventEmitter<CategorySelectOption>();

  @ViewChild('creatorSideBarRef') creatorSideBarRef!: Drawer;

  private translateService = inject(TranslateService);
  private store = inject(Store);

  creatorSideBar = false;

  block_description: any;
  searchData: string | undefined = undefined;
  loading = toSignal(this.store.select(selectLoading));
  selectedCategory = toSignal(this.store.select(selectSelectedCategoryCommonSelectOption));
  categories = toSignal(this.store.select(selectCategoriesCommonSelectOption), {
    initialValue: [] as CategorySelectOption[],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['category_type']) {
      const searchData = {
        category_type: this.category_type,
      };

      this.store.dispatch(
        CategoryPathActions.loadCategories({
          page: 1,
          pageSize: 50,
          searchData,
          selectFirst: this.selectFirst,
        }),
      );
    }
  }

  ngOnInit(): void {
    if (this.parameters) {
      this.category_type = this.parameters.category_type;
      if (this.parameters.formControl) {
        this.categoryFormControl = this.parameters.formControl;
      } else {
        this.categoryFormControl = new FormControl();
      }
    }

    switch (this.description) {
      case 'product_create_form':
        this.block_description = this.translateService.instant(
          'Identify your plant from our system database to automatically reach customers in our Plants and Garden app.',
        );
        break;
    }

    this.initializeComponent();
    if (this.categoryFormControl.value) {
      this.store.dispatch(
        CategoryPathActions.selectCategory({
          categoryId: Number(this.categoryFormControl.value),
        }),
      );
      this.categoryFormControl.valueChanges.pipe().subscribe((value) => {
        this.store.dispatch(
          CategoryPathActions.selectCategory({
            categoryId: Number(value),
          }),
        );
      });
    }
  }

  private initializeComponent() {
    // Prepare search data with category type
    const searchData = {
      category_type: this.category_type,
    };

    // Load initial data from store
    this.store.dispatch(
      CategoryPathActions.loadCategories({
        page: 1,
        pageSize: 50,
        searchData,
        selectFirst: this.selectFirst,
      }),
    );
  }

  select(selectedCategory: CategorySelectOption) {
    if (selectedCategory && selectedCategory.id !== undefined) {
      this.categoryFormControl.setValue(selectedCategory.id, { emitEvent: true });
      this.selectedCategoryEvent.emit(selectedCategory);
      this.store.dispatch(CategoryPathActions.selectCategory({ categoryId: selectedCategory.id }));
    }
  }

  getClosePanel() {
    this.creatorSideBar = false;
  }

  createNew() {
    this.creatorSideBar = true;
  }

  savedForm(data: any) {
    if (data) {
      this.store.dispatch(CategoryPathActions.createCategory({ category: data }));
    }
    this.getClosePanel();
  }

  onPaginationChange(event: PaginationAndSearch) {
    this.store.dispatch(
      CategoryPathActions.loadCategories({
        page: event.page,
        pageSize: 50,
        searchData: {
          category_type: this.category_type,
          keyword_translation: event.searchString,
        },
        appendData: true,
      }),
    );
  }
  onSearchChange(event: PaginationAndSearch) {
    this.store.dispatch(
      CategoryPathActions.loadCategories({
        page: 1,
        pageSize: 50,
        searchData: {
          category_type: this.category_type,
          keyword_translation: event.searchString,
        },
        appendData: false,
      }),
    );
  }
}
